<template>
    <div
        class="sticky top-2 z-50 col-span-full row-start-1 lg:top-5 lg:col-span-4 lg:col-start-1 lg:row-start-1 lg:row-end-auto">
        <nav class="tile-blur-heavy tile relative overflow-hidden">
            <button
                :aria-expanded="isOpen"
                aria-controls="navigation-list"
                class="flex w-full items-center justify-between"
                @click="isOpen = !isOpen">
                <h2 class="text-left text-lg lg:text-xl">{{ title }}</h2>
                <SvgComponent
                    class="block w-4 transition-transform duration-300 lg:hidden"
                    :class="{ 'rotate-x-180': isOpen }"
                    icon="chevron-down" />
            </button>
            <span v-show="!isOpen" class="lg:hidden">{{
                activeSection?.key ? activeSection.key : $t('selectSection')
            }}</span>
            <div
                id="navigation-list"
                class="grid grid-rows-[0fr] overflow-hidden transition-all duration-300"
                :class="{
                    'mt-4 grid-rows-[1fr]': isOpen || breakpoints[viewport] >= breakpoints.lg,
                }"
                role="region">
                <ul class="overflow-hidden">
                    <li
                        v-for="link in items"
                        :key="link.id"
                        class="mb-4 overflow-hidden pl-2 last-of-type:mb-0"
                        :class="{
                            'border-l-2 border-white !pl-1.5': activeSection?.value === link.id,
                        }">
                        <button
                            class="cursor-pointer"
                            @click="(e: Event) => scrollToActiveSection(e, link)">
                            {{ link.title }}
                        </button>
                    </li>
                </ul>
            </div>
        </nav>
    </div>
</template>
<script lang="ts" setup>
import type { Item, SideNavigationModel } from '@su4r-frontend/api';

defineProps<SideNavigationModel>();
const { viewport, breakpoints } = useViewport();
const sectionTitles = ref<NodeListOf<HTMLHeadingElement> | null>(null);
const isOpen = ref(false);
const activeSection = ref<{ key?: string; value?: string }>();
let observer: IntersectionObserver | null = null;

const updateHashToUrl = (id: string): void => {
    if (window.location.hash !== `#${id}`) {
        window.history.replaceState(null, '', `#${id}`);
    }
};

const scrollToActiveSection = (e: Event, link: Item): void => {
    activeSection.value = { key: link.title, value: link.id };
    updateHashToUrl(link.id ?? '');
    isOpen.value = false;
    if (!sectionTitles?.value) return;
    Array.from(sectionTitles?.value)
        ?.find((section: HTMLHeadingElement) => section.id === link.id)
        ?.scrollIntoView({ behavior: 'smooth' });
};

const initializeObserver = (): void => {
    if (!import.meta.client) return;

    const observerOptions = {
        root: null,
        rootMargin: '0px 0px -40% 0px',
        threshold: 1,
    };

    observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const target = entry.target as HTMLHeadingElement;
            if (entry.isIntersecting) {
                activeSection.value = { key: target.innerText, value: target.id };
                updateHashToUrl(target.id);
            }
        });
    }, observerOptions);

    sectionTitles?.value?.forEach((section) => {
        observer?.observe(section);
    });
};

onMounted(() => {
    sectionTitles.value = document.querySelectorAll<HTMLHeadingElement>('[data-section-title]');
    initializeObserver();
});

onUnmounted(() => {
    if (import.meta.client && observer) {
        sectionTitles?.value?.forEach((section) => {
            observer?.unobserve(section);
        });
        observer.disconnect();
        observer = null;
    }
});
</script>
